import React from 'react';

import {
    postMessageRN,
    runningInRNWebView,
    useReactNativeProps,
    WebToRNAction,
} from '@eika-infrastruktur/mobilbank-lib';
import { Label } from '@eika/label';
import { LinkList } from '@eika/link-list';

import { Link, useConfig } from '../../../defaultConfig';
import type { Context } from '../../../generated-types';
import useTranslation from '../../../hooks/useTranslation';
import { settingsTrackIDs } from '../../../utils/gtm/trackIDs/settings';

const getLinkText = (appContext: Context) => (link: Link, translatedTextPM: string, translatedTextBM?: string) => {
    if (link.text && link.text.length > 0) {
        return link.text;
    }

    if (appContext === 'BUSINESS') {
        return translatedTextBM ?? translatedTextPM;
    }

    return translatedTextPM;
};

const Adaptations: React.FC = () => {
    const translated = useTranslation();
    const { supports } = useReactNativeProps();
    const {
        editAccountsLink,
        editSalaryInfoStartPageLink,
        loginSettingsLink,
        paymentSettingsLink,
        contactInfo,
        myConsentsLink,
        changeLanguagePageLink,
        notificationsSettingsLink,
        featureToggles,
        appContext,
    } = useConfig();

    const getText = getLinkText(appContext);

    return (
        <>
            <h3 className="settings__title">{translated.settings.adaptations}</h3>
            <LinkList className="settings__link-list">
                {editAccountsLink?.url && (
                    <LinkList.Item
                        href={editAccountsLink.url}
                        description={translated.settings.accountDescription}
                        data-track-id={settingsTrackIDs.link}
                        data-track-linkname={getText(editAccountsLink, translated.settings.account)}
                    >
                        {getText(editAccountsLink, translated.settings.account)}
                    </LinkList.Item>
                )}
                {!featureToggles.disableNextSalary && editSalaryInfoStartPageLink?.url && (
                    <LinkList.Item
                        href={editSalaryInfoStartPageLink.url}
                        description={translated.settings.nextSalarySubtitle}
                        data-track-id={settingsTrackIDs.link}
                        data-track-linkname={getText(editSalaryInfoStartPageLink, translated.settings.nextSalary)}
                    >
                        {getText(editSalaryInfoStartPageLink, translated.settings.nextSalary)}
                    </LinkList.Item>
                )}
                {runningInRNWebView && loginSettingsLink?.url && (
                    <LinkList.Item href={loginSettingsLink.url}>
                        {getText(loginSettingsLink, translated.settings.login)}
                    </LinkList.Item>
                )}
                {paymentSettingsLink?.url && (
                    <LinkList.Item
                        href={paymentSettingsLink.url}
                        description={
                            !(featureToggles.disableEfaktura || featureToggles.disableAvtalegiro)
                                ? translated.settings.paymentSettingsSubtitle
                                : undefined
                        }
                    >
                        {getText(paymentSettingsLink, translated.settings.paymentSettings)}
                    </LinkList.Item>
                )}
                {runningInRNWebView &&
                    featureToggles.enablePushNotifications &&
                    supports?.pushNotificationsV2 &&
                    notificationsSettingsLink && (
                        <LinkList.Item
                            href={notificationsSettingsLink.url}
                            labels={<Label text={translated.new} type="attention" size="small" />}
                            data-track-id={settingsTrackIDs.link}
                            data-track-linkname={translated.settings.pushNotifications}
                        >
                            {translated.settings.pushNotifications}
                        </LinkList.Item>
                    )}
                {contactInfo?.url && (
                    <LinkList.Item
                        href={contactInfo.url}
                        data-track-id={settingsTrackIDs.link}
                        data-track-linkname={getText(
                            contactInfo,
                            translated.settings.contactInfo,
                            translated.settings.contactInfoBM,
                        )}
                    >
                        {getText(contactInfo, translated.settings.contactInfo, translated.settings.contactInfoBM)}
                    </LinkList.Item>
                )}
                {myConsentsLink?.url && (
                    <LinkList.Item
                        href={myConsentsLink.url}
                        data-track-id={settingsTrackIDs.link}
                        data-track-linkname={getText(myConsentsLink, translated.settings.myConsents)}
                    >
                        {getText(myConsentsLink, translated.settings.myConsents)}
                    </LinkList.Item>
                )}
                {runningInRNWebView && featureToggles.enableLanguageSelection && changeLanguagePageLink?.url && (
                    <LinkList.Item
                        href={changeLanguagePageLink.url}
                        data-track-id={settingsTrackIDs.link}
                        data-track-linkname={getText(changeLanguagePageLink, translated.settings.language)}
                    >
                        {getText(changeLanguagePageLink, translated.settings.language)}
                    </LinkList.Item>
                )}
                {runningInRNWebView && featureToggles.enableChangeAppIcon && supports?.changeAppIcon && (
                    <LinkList.ItemAsButton
                        onClick={() => postMessageRN({ action: WebToRNAction.OPEN_CHANGE_APPICON, payload: undefined })}
                        labels={<Label text={translated.new} type="attention" size="small" />}
                        data-track-id={settingsTrackIDs.link}
                        data-track-linkname={translated.settings.changeAppIcon}
                    >
                        {translated.settings.changeAppIcon}
                    </LinkList.ItemAsButton>
                )}
            </LinkList>
        </>
    );
};

export default Adaptations;
